import React from 'react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ContactForm from '../components/ContactForm';
import { COMPANY_INFO } from '../data/siteData';

export default function DeutschPage() {
  return (
    <>
      <SEO
        title="Deutsch - Hærvejens Træpleje"
        description="Kontaktieren Sie Hærvejens Træpleje – Zertifizierte Baumpflege in Ostjütland und im Dreiecksgebiet. Sicher, fachgerecht und schonend."
        canonical="https://xn--hrvejenstrpleje-xlbj.dk/deutsch/"
        ogImage="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/topkapning-i-fredericia.jpg"
      />

      <Breadcrumbs items={[{ label: 'Deutsch' }]} />

      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Deutsche Informationen
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Kontaktieren Sie Hærvejens Træpleje – Zertifizierte Baumpflege in Ostjütland und im Dreiecksgebiet
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            Willkommen auf der deutschen Kontaktseite von Hærvejens Træpleje. Wir sind Ihr professioneller Partner, wenn es um sichere, fachgerechte und schonende Baumpflege geht.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6 text-[#35483d] text-base leading-relaxed">
            <div className="rounded-2xl overflow-hidden border border-[#dce5df] mb-6">
              <img
                src="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traefaeldning-i-horsens-1.jpg"
                alt="Træfældning i Horsens"
                className="w-full h-auto object-cover"
              />
            </div>

            <p>
              Unser Team besteht aus 12 erfahrenen und zertifizierten Baumpflegern (Arboristen), die an der dänischen Forstschule (Skovskolen) ausgebildet wurden. Wir legen größten Wert auf gutes Handwerk, gründliche Beratung und absolute Sicherheit.
            </p>

            <p>
              Für uns ist Baumpflege mehr als nur der Umgang mit der Motorsäge. Es geht darum, die Biologie des Baumes zu verstehen, jede Aufgabe mit geschultem Auge zu beurteilen und die Arbeit mit einer Präzision auszuführen, die Ihre Werte schützt und Ihren Garten in perfektem Zustand hinterlässt.
            </p>

            <h2 className="text-2xl font-bold text-[#14291d] pt-4">Unsere Baumpflege-Dienstleistungen</h2>
            <p>
              Wir bieten ein umfassendes Spektrum an Dienstleistungen rund um den Baum. Egal, ob ein Baum zu groß geworden ist, krank ist oder von einem neuen Projekt weichen muss – wir haben die richtige Lösung für Sie:
            </p>

            <ul className="space-y-3 list-disc pl-5">
              <li>
                <strong>Baumfällung:</strong> Sichere und kontrollierte Fällung von Bäumen jeder Größe. Wir analysieren Neigung, Aststruktur und Umgebung, um die sicherste Fällrichtung zu bestimmen.
              </li>
              <li>
                <strong>Kronenkappung (Topkapning):</strong> Wenn nicht genug Platz für eine normale Fällung vorhanden ist, tragen wir den Baum stückweise von oben nach unten ab. Dies ist besonders in dicht bebauten Gebieten oder in der Nähe von Stromleitungen wichtig.
              </li>
              <li>
                <strong>Baumbeschnitt:</strong> Fachgerechter Rückschnitt zur Förderung der Baumgesundheit, zur Entfernung toter Äste oder um mehr Licht in Ihren Garten zu lassen.
              </li>
              <li>
                <strong>Wurzelfräsen (Stubfræsning):</strong> Nach der Fällung entfernen wir den verbleibenden Baumstumpf und die Wurzeln mit einer effektiven Wurzelfräse, so dass Sie den Platz sofort wieder benutzen können.
              </li>
              <li>
                <strong>Hackschnitzelerzeugung und Aufräumarbeiten:</strong> Auf Wunsch verwandeln wir Äste in Hackschnitzel für Ihre Beete und hinterlassen Ihren Garten stets sauber und aufgeräumt.
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-[#14291d] pt-4">Ihre Sicherheit ist unsere Priorität</h2>
            <ul className="space-y-2 list-disc pl-5">
              <li>
                <strong>Vollständig versichert:</strong> Wir sind über Topdanmark für bis zu 10 Millionen DKK versichert.
              </li>
              <li>
                <strong>Höchste Sicherheitsstandards:</strong> Wir halten uns strikt an die strengen Regeln der dänischen Arbeitsschutzbehörde (Arbejdstilsynet) für Sicherheit und gewerbliches Klettern.
              </li>
              <li>
                <strong>Lokale Expertise:</strong> Wir sind tief in der jütländischen Erde verwurzelt und bedienen Städte wie Aarhus, Skanderborg, Horsens, Vejle, Fredericia und Kolding.
              </li>
            </ul>

            <div className="bg-[#f2f7f4] rounded-2xl p-6 border border-[#cfe0d6] mt-8">
              <h3 className="text-lg font-bold text-[#14291d] mb-2">Kontaktdaten</h3>
              <p className="text-sm"><strong>Hærvejens Træpleje</strong></p>
              <p className="text-sm">Ansprechpartner: Mathias Svendsen (Inhaber)</p>
              <p className="text-sm">Adresse: {COMPANY_INFO.address}, {COMPANY_INFO.postalCode} {COMPANY_INFO.city}, Dänemark</p>
              <p className="text-sm">Telefon: <a href="tel:+4527135787" className="font-bold underline">+45 27 13 57 87</a></p>
              <p className="text-sm">E-Mail: <a href={`mailto:${COMPANY_INFO.email}`} className="font-bold underline">{COMPANY_INFO.email}</a></p>
              <p className="text-sm">CVR-Nr. (Umsatzsteuer-ID): {COMPANY_INFO.cvr}</p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <ContactForm
              title="Unverbindliches Angebot"
              subtitle="Füllen Sie das untenstehende Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen."
            />
          </div>
        </div>
      </section>
    </>
  );
}
